// src/components/grammar/GrammarExerciseGuide.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  ChevronRight,
  ChevronLeft,
  MousePointer2,
  Hand,
  Keyboard,
  CheckCircle,
  RotateCcw,
  Bookmark,
  ArrowRight,
  Target,
  Move,
  Edit3,
  Link2
} from 'lucide-react';

const GrammarExerciseGuide = ({ isOpen, onClose, onComplete }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [animatePointer, setAnimatePointer] = useState(false);

  // Trigger animations when step changes
  useEffect(() => {
    if (isOpen) {
      setAnimatePointer(false);
      const timer = setTimeout(() => setAnimatePointer(true), 500);
      return () => clearTimeout(timer);
    }
  }, [currentStep, isOpen]);

  const steps = [
    {
      id: 'welcome',
      icon: <Target className="w-12 h-12 text-blue-600" />,
      title: t('grammar.exerciseGuide.welcome.title') || 'Welcome to Grammar Practice!',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 text-lg">
            {t('grammar.exerciseGuide.welcome.description') || 'Learn how to interact with different exercise types and master grammar through practice.'}
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 font-medium text-sm">
              💡 {t('grammar.exerciseGuide.welcome.tip') || 'Each exercise type has unique interaction methods. We\'ll show you exactly how to use them!'}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="bg-purple-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-purple-600">5</p>
              <p className="text-xs text-purple-800">Exercise Types</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-600">3</p>
              <p className="text-xs text-green-800">Action Buttons</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'multiple-choice',
      icon: <MousePointer2 className="w-12 h-12 text-purple-600" />,
      title: t('grammar.exerciseGuide.multipleChoice.title') || 'Multiple Choice Questions',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            {t('grammar.exerciseGuide.multipleChoice.description') || 'Click on the correct answer from the given options.'}
          </p>

          {/* Demo Multiple Choice */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-900 mb-3">
              Example: "Er ____ gut Deutsch sprechen."
            </p>

            <div className="space-y-2 relative">
              {['kann', 'muss', 'will', 'soll'].map((option, idx) => (
                <div
                  key={option}
                  className={`relative p-3 border-2 rounded-lg transition-all ${
                    idx === 0 
                      ? 'border-blue-400 bg-blue-50 animate-pulseGlow' 
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <span className="font-medium">{option}</span>
                  {idx === 0 && animatePointer && (
                    <MousePointer2
                      className="absolute -left-8 top-1/2 -translate-y-1/2 w-6 h-6 text-blue-600 animate-bounce"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
            <p className="text-sm text-blue-900">
              <span className="font-bold">💡 Tip:</span> {t('grammar.exerciseGuide.multipleChoice.tip') || 'Selected answers are highlighted in blue. Click to select your answer.'}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'fill-in-blanks',
      icon: <Keyboard className="w-12 h-12 text-green-600" />,
      title: t('grammar.exerciseGuide.fillInBlanks.title') || 'Fill in the Blanks',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            {t('grammar.exerciseGuide.fillInBlanks.description') || 'Type the missing words directly into the blank spaces.'}
          </p>

          {/* Demo Fill in Blanks */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
            <p className="text-gray-700 text-lg leading-relaxed">
              Ich gehe jeden Tag <span className="relative inline-block">
                <input
                  type="text"
                  className="mx-2 px-3 py-2 border-2 border-blue-400 rounded-lg text-center font-medium min-w-[120px] bg-blue-50 animate-pulseGlow"
                  placeholder="..."
                  readOnly
                />
                {animatePointer && (
                  <Keyboard
                    className="absolute -top-8 left-1/2 -translate-x-1/2 w-6 h-6 text-blue-600 animate-bounce"
                  />
                )}
              </span> Arbeit.
            </p>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">
                {t('grammar.exerciseGuide.fillInBlanks.step1') || 'Click inside the blank field'}
              </p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">
                {t('grammar.exerciseGuide.fillInBlanks.step2') || 'Type your answer'}
              </p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">
                {t('grammar.exerciseGuide.fillInBlanks.step3') || 'Multiple blanks? Fill them all before submitting'}
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'sentence-building',
      icon: <Move className="w-12 h-12 text-orange-600" />,
      title: t('grammar.exerciseGuide.sentenceBuilding.title') || 'Sentence Building',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            {t('grammar.exerciseGuide.sentenceBuilding.description') || 'Tap or click words to build the correct sentence in order.'}
          </p>

          {/* Demo Sentence Building */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 space-y-4">
            {/* Word Bank */}
            <div>
              <p className="text-xs text-gray-600 mb-2 font-medium">
                {t('grammar.exerciseGuide.sentenceBuilding.wordBank') || 'Available Words:'}
              </p>
              <div className="flex flex-wrap gap-2 relative">
                {['Ich', 'ins', 'gehe', 'Kino'].map((word, idx) => (
                  <button
                    key={word}
                    className={`px-4 py-2 bg-white border-2 rounded-lg font-medium transition-all ${
                      idx === 0 
                        ? 'border-orange-400 bg-orange-50 animate-pulseGlow scale-105' 
                        : 'border-gray-300'
                    }`}
                  >
                    {word}
                    {idx === 0 && animatePointer && (
                      <Hand
                        className="absolute -top-8 left-1/2 -translate-x-1/2 w-6 h-6 text-orange-600 animate-bounce"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Sentence Area */}
            <div className="relative">
              <p className="text-xs text-gray-600 mb-2 font-medium">
                {t('grammar.exerciseGuide.sentenceBuilding.sentence') || 'Your Sentence:'}
              </p>
              <div className="min-h-[60px] p-3 bg-white border-2 border-dashed border-green-300 rounded-lg flex items-center">
                <p className="text-gray-400 text-sm italic">
                  {t('grammar.exerciseGuide.sentenceBuilding.tapToStart') || 'Tap words above to build your sentence...'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 border-l-4 border-orange-500 p-3 rounded">
            <p className="text-sm text-orange-900">
              <span className="font-bold">💡 Tip:</span> {t('grammar.exerciseGuide.sentenceBuilding.tip') || 'Tap a word in your sentence to move it back to the word bank!'}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'error-correction',
      icon: <Edit3 className="w-12 h-12 text-red-600" />,
      title: t('grammar.exerciseGuide.errorCorrection.title') || 'Error Correction',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            {t('grammar.exerciseGuide.errorCorrection.description') || 'Find and click the incorrect word, then type the correction.'}
          </p>

          {/* Demo Error Correction */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
            <p className="text-gray-700 text-lg leading-relaxed">
              Ich bin gestern ins Kino{' '}
              <span className="relative inline-block">
                <span className="mx-1 px-2 py-1 bg-red-100 border-2 border-red-300 text-red-700 rounded cursor-pointer animate-pulseGlow">
                  gehst
                </span>
                {animatePointer && (
                  <MousePointer2
                    className="absolute -top-8 left-1/2 -translate-x-1/2 w-6 h-6 text-red-600 animate-bounce"
                  />
                )}
              </span>
              {' '}und einen Film gesehen.
            </p>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <span className="flex-shrink-0 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
              <p className="text-gray-700">
                {t('grammar.exerciseGuide.errorCorrection.step1') || 'Incorrect words are highlighted in red'}
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="flex-shrink-0 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
              <p className="text-gray-700">
                {t('grammar.exerciseGuide.errorCorrection.step2') || 'Click the highlighted word to edit it'}
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="flex-shrink-0 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
              <p className="text-gray-700">
                {t('grammar.exerciseGuide.errorCorrection.step3') || 'Type the correct form'}
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'matching-pairs',
      icon: <Link2 className="w-12 h-12 text-teal-600" />,
      title: t('grammar.exerciseGuide.matchingPairs.title') || 'Matching Pairs',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            {t('grammar.exerciseGuide.matchingPairs.description') || 'Click items from both columns to create matching pairs.'}
          </p>

          {/* Demo Matching Pairs */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Left Column */}
              <div className="space-y-2">
                <p className="text-xs text-gray-600 font-medium mb-2">Column A:</p>
                {['Guten Morgen', 'Danke', 'Auf Wiedersehen'].map((item, idx) => (
                  <button
                    key={item}
                    className={`w-full p-3 text-left border-2 rounded-lg transition-all ${
                      idx === 0 
                        ? 'border-teal-400 bg-teal-50 animate-pulseGlow' 
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    {item}
                    {idx === 0 && animatePointer && (
                      <Hand
                        className="absolute -left-8 top-1/2 -translate-y-1/2 w-6 h-6 text-teal-600 animate-bounce"
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* Right Column */}
              <div className="space-y-2">
                <p className="text-xs text-gray-600 font-medium mb-2">Column B:</p>
                {['Goodbye', 'Thank you', 'Good morning'].map((item) => (
                  <button
                    key={item}
                    className="w-full p-3 text-left border-2 border-gray-300 bg-white rounded-lg"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-teal-50 border-l-4 border-teal-500 p-3 rounded">
            <p className="text-sm text-teal-900">
              <span className="font-bold">💡 Tip:</span> {t('grammar.exerciseGuide.matchingPairs.tip') || 'Click one item from each column to create a pair. Matched pairs turn green!'}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'action-buttons',
      icon: <CheckCircle className="w-12 h-12 text-green-600" />,
      title: t('grammar.exerciseGuide.actionButtons.title') || 'Action Buttons Explained',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            {t('grammar.exerciseGuide.actionButtons.description') || 'Every exercise has helpful buttons to guide your practice.'}
          </p>

          {/* Check Answer Button */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-semibold text-blue-900">
                {t('grammar.exerciseGuide.actionButtons.check.title') || 'Check Answer'}
              </h4>
            </div>
            <p className="text-sm text-blue-800">
              {t('grammar.exerciseGuide.actionButtons.check.description') || 'Submit your answer to see if it\'s correct. Get instant feedback with explanations!'}
            </p>
          </div>

          {/* Reset Button */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gray-500 rounded-lg flex items-center justify-center">
                <RotateCcw className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900">
                {t('grammar.exerciseGuide.actionButtons.reset.title') || 'Reset / Try Again'}
              </h4>
            </div>
            <p className="text-sm text-gray-700">
              {t('grammar.exerciseGuide.actionButtons.reset.description') || 'Clear your answer and start over. Practice makes perfect!'}
            </p>
          </div>

          {/* Next Button */}
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <ArrowRight className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-semibold text-green-900">
                {t('grammar.exerciseGuide.actionButtons.next.title') || 'Next Exercise'}
              </h4>
            </div>
            <p className="text-sm text-green-800">
              {t('grammar.exerciseGuide.actionButtons.next.description') || 'Move to the next question after checking your answer.'}
            </p>
          </div>

          {/* Bookmark Button */}
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                <Bookmark className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-semibold text-yellow-900">
                {t('grammar.exerciseGuide.actionButtons.bookmark.title') || 'Bookmark Question'}
              </h4>
            </div>
            <p className="text-sm text-yellow-800">
              {t('grammar.exerciseGuide.actionButtons.bookmark.description') || 'Save challenging questions to review later from the floating tab bar!'}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'success',
      icon: <CheckCircle className="w-12 h-12 text-green-600" />,
      title: t('grammar.exerciseGuide.success.title') || 'You\'re Ready to Practice!',
      content: (
        <div className="space-y-4">
          <div className="text-center py-4">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounceIn">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <p className="text-gray-700 text-lg mb-4">
              {t('grammar.exerciseGuide.success.description') || 'You now know how to use all exercise types! Start practicing and improve your grammar skills.'}
            </p>
          </div>

          {/* Quick Tips Summary */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 space-y-2">
            <p className="font-semibold text-gray-900 mb-2">
              {t('grammar.exerciseGuide.success.quickTips') || '🎯 Quick Tips:'}
            </p>
            <div className="space-y-1 text-sm text-gray-700">
              <p>✓ {t('grammar.exerciseGuide.success.tip1') || 'Read questions carefully before answering'}</p>
              <p>✓ {t('grammar.exerciseGuide.success.tip2') || 'Use the Reset button if you want to try again'}</p>
              <p>✓ {t('grammar.exerciseGuide.success.tip3') || 'Bookmark difficult questions for later review'}</p>
              <p>✓ {t('grammar.exerciseGuide.success.tip4') || 'Review your mistakes from the floating tab bar'}</p>
            </div>
          </div>

          {/* Celebration */}
          <div className="text-center space-y-2">
            <div className="flex justify-center gap-2">
              <span className="text-3xl animate-bounce">🎉</span>
              <span className="text-3xl animate-bounce animate-delay-100">✨</span>
              <span className="text-3xl animate-bounce animate-delay-200">🚀</span>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const currentStepData = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    if (dontShowAgain && onComplete) {
      onComplete(true);
    }
    onClose();
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 pb-16 sm:pb-4 vocabulary-guide-modal animate-fade-in-fast">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content - Mobile: bottom-sheet style */}
      <div className="relative w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden vocabulary-guide-content animate-scale-in max-h-[92vh] sm:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-4 sm:p-6 flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold flex-1 min-w-0">
              {t('grammar.exerciseGuide.title') || 'Exercise Practice Guide'}
            </h2>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-white/20 rounded-xl transition-colors touch-manipulation min-h-[48px] min-w-[48px] flex items-center justify-center flex-shrink-0"
              aria-label="Close guide"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-orange-100 text-xs sm:text-sm md:text-base mt-1">
            {t('grammar.exerciseGuide.subtitle') || 'Learn how to interact with each exercise type'}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="bg-gray-100 px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">
              {t('grammar.exerciseGuide.progress', { current: currentStep + 1, total: steps.length }) || `Step ${currentStep + 1} of ${steps.length}`}
            </span>
            <span className="text-sm text-gray-600">
              {Math.round(((currentStep + 1) / steps.length) * 100)}%
            </span>
          </div>

          {/* Step Indicators */}
          <div className="flex items-center gap-2">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className="flex-1 h-2 rounded-full overflow-hidden bg-gray-300 transition-all duration-300"
              >
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    index < currentStep
                      ? 'bg-gradient-to-r from-green-500 to-green-600 w-full'
                      : index === currentStep
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 w-full'
                      : 'bg-gray-300 w-0'
                  }`}
                  style={{
                    width: index <= currentStep ? '100%' : '0%'
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Content Area - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8 min-h-0">
          <div className="space-y-4 sm:space-y-6">
            {/* Icon */}
            <div className="flex justify-center animate-bounceIn">
              {currentStepData.icon}
            </div>

            {/* Title */}
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 text-center animate-fadeIn">
              {currentStepData.title}
            </h3>

            {/* Content */}
            <div className="animate-fadeIn">
              {currentStepData.content}
            </div>
          </div>
        </div>

        {/* Footer Actions - Mobile: touch targets 48px, checkbox on every step */}
        <div className="bg-gray-50 border-t border-gray-200 p-4 sm:p-6 flex-shrink-0">
          {/* Don't Show Again Checkbox - visible on every step */}
          <label className="flex items-center gap-3 mb-4 cursor-pointer touch-manipulation min-h-[48px]">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500 flex-shrink-0"
            />
            <span className="text-sm sm:text-base text-gray-600">
              {t('grammar.exerciseGuide.dontShowAgain') || "Don't show this guide again"}
            </span>
          </label>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={handlePrevious}
              disabled={isFirstStep}
              className={`flex items-center gap-2 px-4 py-3 sm:py-2 rounded-xl font-medium transition-all touch-manipulation min-h-[48px] ${
                isFirstStep
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden sm:inline">{t('grammar.exerciseGuide.previous') || 'Previous'}</span>
            </button>

            {isLastStep ? (
              <button
                onClick={handleFinish}
                className="flex items-center gap-2 px-5 sm:px-6 py-3 min-h-[48px] bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 touch-manipulation"
              >
                <CheckCircle className="w-5 h-5" />
                {t('grammar.exerciseGuide.startPracticing') || 'Start Practicing'}
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-5 sm:px-6 py-3 min-h-[48px] bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-semibold hover:from-orange-600 hover:to-red-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 touch-manipulation"
              >
                {t('grammar.exerciseGuide.next') || 'Next'}
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrammarExerciseGuide;